import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  formatDate as fd,
  formatDateAndTime as fdt,
} from '@fulcrum/lib';
import { t } from 'i18next';
import { DateTime } from 'luxon';

export function toISO8601Date(date: Date): string {
  // Sweden uses the yyyy-mm-dd date format
  return date.toLocaleDateString('sv-SE');
}

/**
 * Formats the given Date into a medium length datetime string (abbreviated month)
 * DateTime.DATETIME_MED - format like 'Oct 14, 1983, 9:30 AM'. Only 12-hour if the locale is.
 */
export const toMedDateTimeString = (
  dateTime?: Date | null,
  locale: string = 'en-US'
): string => {
  if (!dateTime) return '';
  const date = DateTime.fromJSDate(dateTime);
  return date.toLocaleString(DateTime.DATETIME_MED, { locale });
};

/**
 * Format a date with user names using the current locale.
 *
 * @param date - The date to format.
 * @param firstName - The first name of the user.
 * @param lastName - The last name of the user.
 * @returns The formatted date string.
 */
export function getFormattedDate(
  date: string,
  firstName: string,
  lastName: string
): string {
  // NOTE: This utility is copied from `apps\tcweb\web\src\utils.ts` and is implemented partially.

  // TODO: Implement the rest of the original utility, which will
  // hide user names based on the current user's role.

  const formatString = t('general.dateFormat');
  return formatString
    .replace('{{date}}', formatDate(date))
    .replace('{{user}}', [firstName, lastName].join(' '));
}

/**
 * Format a date to a string using the current user's locale, timeZoneOffset and preferred date format.
 *
 * @param date - The date to format.
 * @returns The formatted date string.
 */
export function formatDate(date: string): string {
  // TODO: Once user profile and settings are available, use the current user's
  // locale, timeZoneOffset and preferred date format.
  return fd(date, undefined, undefined, DEFAULT_DATE_FORMAT);
}

/**
 * Format a date and time to a string using the current user's locale, timeZoneOffset and preferred date format.
 *
 * @param date - The date to format.
 * @returns The formatted date string.
 */
export function formatDateAndTime(date: string): string {
  // TODO: Once user profile and settings are available, use the current user's
  // locale, timeZoneOffset and preferred date format.
  return fdt(date, undefined, undefined, {
    date: DEFAULT_DATE_FORMAT,
    time: DEFAULT_TIME_FORMAT,
  });
}

/**
 * Converts MM/DD/YYYY date format to ISO date format (YYYY-MM-DD)
 * Used to convert API date format to date picker format
 *
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Date string in YYYY-MM-DD format, or undefined if invalid
 *
 * @example
 * convertMMDDYYYYtoISO('12/25/2023') // Returns '2023-12-25'
 */
export const convertMMDDYYYYtoISO = (
  dateStr: string | undefined
): string | undefined => {
  if (!dateStr) return undefined;

  try {
    const EXPECTED_PARTS = 3;
    const PAD_LENGTH = 2;
    const MONTH_INDEX = 0;
    const DAY_INDEX = 1;
    const YEAR_INDEX = 2;
    const MIN_MONTH = 1;
    const MAX_MONTH = 12;
    const MIN_DAY = 1;
    const MAX_DAY = 31;

    // Parse MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length !== EXPECTED_PARTS) return undefined;

    const month = parts[MONTH_INDEX].padStart(PAD_LENGTH, '0');
    const day = parts[DAY_INDEX].padStart(PAD_LENGTH, '0');
    const year = parts[YEAR_INDEX];

    // Validate the parsed values
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);

    if (
      isNaN(monthNum) ||
      isNaN(dayNum) ||
      isNaN(yearNum) ||
      monthNum < MIN_MONTH ||
      monthNum > MAX_MONTH ||
      dayNum < MIN_DAY ||
      dayNum > MAX_DAY
    ) {
      return undefined;
    }

    // Return ISO format (YYYY-MM-DD)
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error converting MM/DD/YYYY to ISO:', error);
    return undefined;
  }
};

/**
 * Converts ISO date format (YYYY-MM-DD) to MM/DD/YYYY
 * Used to convert date picker format to API format
 *
 * @param dateInput - Date string in ISO format or Date object
 * @returns Date string in MM/DD/YYYY format, or undefined if invalid
 *
 * @example
 * convertISOtoMMDDYYYY('2023-12-25') // Returns '12/25/2023'
 * convertISOtoMMDDYYYY(new Date(2023, 11, 25)) // Returns '12/25/2023'
 */
export const convertISOtoMMDDYYYY = (
  dateInput: string | Date | undefined
): string | undefined => {
  if (!dateInput) return undefined;

  try {
    const PAD_LENGTH = 2;
    const EXPECTED_PARTS = 3;

    // Handle Date object
    if (dateInput instanceof Date) {
      // Use UTC to avoid timezone shifts
      const year = dateInput.getUTCFullYear();
      const month = String(dateInput.getUTCMonth() + 1).padStart(
        PAD_LENGTH,
        '0'
      );
      const day = String(dateInput.getUTCDate()).padStart(PAD_LENGTH, '0');
      return `${month}/${day}/${year}`;
    }

    const dateStr: string = dateInput;

    // If already in MM/DD/YYYY format, validate and return
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return dateStr;
    }

    // If in ISO format (YYYY-MM-DD), convert to MM/DD/YYYY
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.split('T')[0].split('-');
      if (parts.length === EXPECTED_PARTS) {
        const [year, month, day] = parts;
        return `${month}/${day}/${year}`;
      }
    }

    // Try parsing as date string
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(PAD_LENGTH, '0');
      const day = String(date.getUTCDate()).padStart(PAD_LENGTH, '0');
      return `${month}/${day}/${year}`;
    }

    return undefined;
  } catch (error) {
    console.error('Error converting ISO to MM/DD/YYYY:', error);
    return undefined;
  }
};

/**
 * Formats a date to MM/DD/YYYY format
 * @deprecated Use convertISOtoMMDDYYYY instead for better clarity
 *
 * @param dateInput - Date string or Date object
 * @returns Date string in MM/DD/YYYY format
 */
export const formatDateToMMDDYYYY = (
  dateInput: string | Date | undefined
): string | undefined => {
  return convertISOtoMMDDYYYY(dateInput);
};

/**
 * Parses MM/DD/YYYY format and converts it to a localized display string
 * Formats the date as "Oct 25, 2025" style
 *
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Formatted date string for display, or original string if parsing fails
 *
 * @example
 * parseDateForDisplay('12/25/2023') // Returns 'Dec 25, 2023'
 */
export const parseDateForDisplay = (dateStr: string | undefined): string => {
  if (!dateStr) return '';

  try {
    const EXPECTED_PARTS = 3;
    const RADIX = 10;

    // Parse MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === EXPECTED_PARTS) {
      const [month, day, year] = parts;
      const date = new Date(
        parseInt(year, RADIX),
        parseInt(month, RADIX) - 1,
        parseInt(day, RADIX)
      );

      // Check if date is valid
      if (!isNaN(date.getTime())) {
        // Format as "Oct 25, 2025"
        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        };
        return date.toLocaleDateString('en-US', options);
      }
    }

    return dateStr;
  } catch (error) {
    console.error('Error parsing date:', error);
    return dateStr;
  }
};

/**
 * Formats an ISO 8601 timestamp string into a human-readable date/time string
 * in the user's timezone and locale.
 *
 * @param isoString - ISO 8601 formatted date string
 * @param atConnector - Connector word between date and time (e.g., "at", "à", "um")
 * @returns Formatted date string with timezone (e.g., "14 Dec 2025 at 12:05:03 EST"),
 *          or "Invalid Date" if parsing fails, or empty string if undefined
 *
 * @example
 * formatTimestampWithTimezone('2025-11-21T10:30:45.123Z', 'at')
 * // Returns "21 Nov 2025 at 10:30:45 EST" (in EST timezone)
 */
export const formatTimestampWithTimezone = (
  isoString: string | undefined,
  atConnector: string = 'at'
): string => {
  if (!isoString) return '';

  try {
    // Use Luxon for reliable date/time formatting across locales
    const dt = DateTime.fromISO(isoString);

    // Check if date is valid
    if (!dt.isValid) {
      return 'Invalid Date';
    }

    // Use the user's locale from the browser
    const userLocale = navigator.language || 'en-US';

    // Format: DD MMM YYYY at HH:MM:SS TZ
    // Example: "21 Nov 2025 at 10:30:45 EST"
    const formatted = dt.toFormat('dd LLL yyyy', { locale: userLocale });
    const time = dt.toFormat('HH:mm:ss');
    const timezone = dt.toFormat('ZZZZ'); // Timezone abbreviation (e.g., EST, PST, UTC)

    return `${formatted} ${atConnector} ${time} ${timezone}`;
  } catch {
    return 'Invalid Date';
  }
};
